/**
 * WizardProgress — top-of-wizard navigation bar.
 *
 * Shows:
 * 1. Phase tabs row — completed phases get a check, current gets cot-blue underline.
 * 2. Section indicator pill — "Risk Assessment" or "Complexity Assessment".
 * 3. Question counter text.
 * 4. Animated progress bar (shadcn Progress component).
 */

import { Check } from 'lucide-react';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import type { PhaseId, SectionType } from '@/types/rca';
import { PHASE_NAMES } from '@/data/rcaQuestions';

// ============================================================
// Types
// ============================================================

interface WizardProgressProps {
  activePhases: PhaseId[];
  currentPhaseIdx: number;
  currentSection: SectionType;
  currentQuestionIdx: number;
  totalQuestionsInSection: number;
}

// ============================================================
// Component
// ============================================================

export function WizardProgress({
  activePhases,
  currentPhaseIdx,
  currentSection,
  currentQuestionIdx,
  totalQuestionsInSection,
}: WizardProgressProps) {
  const questionNumber = currentQuestionIdx + 1;
  const progressPercent =
    totalQuestionsInSection > 0
      ? Math.round((questionNumber / totalQuestionsInSection) * 100)
      : 0;

  return (
    <div className="w-full space-y-4 pb-4 border-b border-border">
      {/* Phase tabs row */}
      <div className="flex items-center gap-1 overflow-x-auto pb-1">
        {activePhases.map((phaseId, idx) => {
          const isCompleted = idx < currentPhaseIdx;
          const isCurrent = idx === currentPhaseIdx;
          const isFuture = idx > currentPhaseIdx;

          return (
            <div
              key={phaseId}
              className={cn(
                'flex items-center gap-1.5 px-3 py-2 rounded-md text-sm whitespace-nowrap transition-all duration-200',
                'border-b-2',
                isCurrent && 'border-cot-blue text-cot-blue font-semibold bg-cot-blue/5',
                isCompleted && 'border-risk-low text-risk-low font-medium',
                isFuture && 'border-transparent text-muted-foreground/50',
              )}
            >
              {isCompleted && (
                <Check className="w-3.5 h-3.5 text-risk-low flex-shrink-0" strokeWidth={2.5} />
              )}
              <span>{PHASE_NAMES[phaseId]}</span>
            </div>
          );
        })}
      </div>

      {/* Section + question info row */}
      <div className="flex items-center justify-between gap-3">
        <Badge
          variant="secondary"
          className={cn(
            'text-xs font-semibold tracking-wide uppercase px-3 py-1',
            currentSection === 'risk'
              ? 'bg-risk-high/10 text-risk-high border border-risk-high/20'
              : 'bg-cot-blue/10 text-cot-blue border border-cot-blue/20',
          )}
        >
          {currentSection === 'risk' ? 'Risk Assessment' : 'Complexity Assessment'}
        </Badge>

        <span className="text-sm text-muted-foreground whitespace-nowrap">
          Question{' '}
          <span className="font-semibold text-foreground">{questionNumber}</span>
          {' '}of{' '}
          <span className="font-semibold text-foreground">{totalQuestionsInSection}</span>
        </span>
      </div>

      {/* Progress bar */}
      <Progress
        value={progressPercent}
        className="h-1.5 bg-muted [&>div]:bg-cot-blue [&>div]:transition-all [&>div]:duration-500"
      />
    </div>
  );
}
